// src/components/auth/PrivateRoute.js
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Alert, Box, Typography, Button, CircularProgress } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useAuth } from '../../contexts/AuthContext';
import AuthDebug from './AuthDebug'; // Importar componente de diagnóstico

const PrivateRoute = ({ children, permission = null }) => {
  const { user, loading, refreshAuth, hasPermission } = useAuth();
  const location = useLocation();

  // MODO DIAGNÓSTICO - Establece a false después de resolver el problema
  const DIAGNOSTICS_MODE = false;

  // Mostrar componente de diagnóstico en modo diagnóstico
  const showDiagnostics = DIAGNOSTICS_MODE;

  // Esta línea es crítica - Verifica el token directamente desde localStorage
  // para evitar problemas cuando se abre en una nueva pestaña
  const token = localStorage.getItem('token');

  // Pantalla de carga mejorada
  if (loading) {
    return (
      <Box className="flex items-center justify-center min-h-screen">
        <Box className="text-center">
          <CircularProgress size={60} className="mb-4" />
          <Typography variant="h6">Cargando...</Typography>
        </Box>
      </Box>
    );
  }

  console.log('PrivateRoute - Verificando acceso:', {
    isAuthenticated: !!user || !!token, // Comprueba user o token
    userRole: user?.role,
    hasToken: !!token,
    requiredPermission: permission,
    userPermissions: user?.permissions,
    isSystemRole: user?.isSystemRole,
    pathname: location.pathname
  });

  // Si no hay usuario ni token, redirigir al login
  if (!user && !token) {
    console.log('Usuario no autenticado y sin token, redirigiendo a login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Si hay token pero no hay usuario, intentar recargar la autenticación
  if (!user && token) {
    console.log('Token presente pero no hay datos de usuario, intentando recargar autenticación');
    refreshAuth();
    return (
      <Box className="flex items-center justify-center min-h-screen">
        <Box className="text-center">
          <CircularProgress size={60} className="mb-4" />
          <Typography variant="h6">Recuperando sesión...</Typography>
        </Box>
      </Box>
    );
  }

  // En modo diagnóstico, permitir acceso sin verificar permisos
  if (DIAGNOSTICS_MODE) {
    // Mostrar advertencia pero permitir acceso
    console.log('MODO DIAGNÓSTICO: Omitiendo verificación de permisos');

    return (
      <>
        {showDiagnostics && <AuthDebug />}
        {children}
      </>
    );
  }

  // Si no se requiere un permiso específico, solo verificamos la autenticación
  if (!permission) {
    return children;
  }

  // Si es admin con rol de sistema, tiene acceso a todo
  if (user?.role === 'admin' && user?.isSystemRole === true) {
    return children;
  }

  // Para otros roles, verificar el permiso específico
  const hasRequiredPermission = hasPermission ? hasPermission(permission) : false;

  if (!hasRequiredPermission) {
    console.log('Usuario sin permiso requerido:', {
      requiredPermission: permission,
      userPermissions: user?.permissions || []
    });

    return (
      <Box className="p-8 flex flex-col items-center justify-center min-h-[400px]">
        <ErrorOutlineIcon color="error" sx={{ fontSize: 64, mb: 2 }} />
        <Typography variant="h5" color="error" gutterBottom>
          Acceso Denegado
        </Typography>
        <Alert severity="warning" className="mb-4">
          No tiene los permisos necesarios para acceder a esta sección.
          <br />
          Se requiere el permiso: <strong>{permission}</strong>
        </Alert>
        <Box className="flex gap-3">
          <Button
            variant="contained"
            color="primary"
            onClick={() => window.history.back()}
          >
            Volver Atrás
          </Button>
          <Button
            variant="outlined"
            onClick={refreshAuth}
          >
            Refrescar Sesión
          </Button>
        </Box>

        {/* Mostrar diagnóstico incluso en caso de error de permisos */}
        {showDiagnostics && <AuthDebug />}
      </Box>
    );
  }

  return children;
};

export default PrivateRoute;